import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@chakra-ui/react';
import { useDrag } from '@use-gesture/react';
import ArrowIcon from './arrow.svg?react';
import DashArrowIcon from './dash-arrow.svg?react';
import useStars from '../hooks/useStars';
import usePrevious from 'use-previous';
import { useThemeColors } from '../hooks/useThemeColors';
import { twMerge } from 'tailwind-merge';

interface FeedItemCarouselProps {
  children: React.ReactNode;
  title?: string | React.ReactNode;
  accent?: string;
  height?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  bottomNode?: React.ReactNode;
  withStars?: boolean;
  itemWidth?: number;
  lastNode?: React.ReactNode;
  firstNode?: React.ReactNode;
  titleNode?: React.ReactNode;
}

const FeedItemCarousel = ({
  children,
  title,
  accent,
  height,
  containerProps = {},
  bottomNode = null,
  withStars = true,
  itemWidth = 341,
  lastNode = null,
  firstNode = null,
  titleNode
}: FeedItemCarouselProps) => {
  const { className, ...restContainerProps } = containerProps;
  const [currentIndex, setCurrentIndex] = useState(0);
  const gap = 24;
  const itemCount = React.Children.count(children);
  const [isWideScreen] = useMediaQuery('(min-width: 1280px)', {
    ssr: true,
    fallback: false,
  });
  const stars = useStars(isWideScreen, false, 8, true);

  const handleNext = () => {
    if (currentIndex < itemCount - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevTitle = usePrevious(title);

  useEffect(() => {
    if (currentIndex !== 0 && prevTitle !== title) {
      setCurrentIndex(0);
    }
  }, [title]);

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const bind = useDrag(
    ({ down, movement: [mx], direction: [xDir], distance: [dx], cancel, touches }) => {
      if (touches > 1) return;
      if (down && dx > itemWidth / 4) {
        if (xDir < 0 && currentIndex < itemCount - 1) {
          setCurrentIndex(currentIndex + 1);
        } else if (xDir > 0 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
        cancel();
      }
    },
  );

  const themeColors = useThemeColors();

  return (
    <div
      className={twMerge(
        'relative overflow-hidden w-full min-w-full max-w-full transition-colors duration-500',
        'border border-dashed border-brand-main',
        'px-[12px] py-[24px] sm:px-[24px] bg-brand-background',
        className
      )}
      style={{
        backgroundColor: accent,
        height,
      }}
      {...restContainerProps}
    >
      {withStars && (
        <div className="pointer-events-none absolute inset-0 z-[1000]">
          {stars}
        </div>
      )}
      <div className="flex justify-between mb-[24px] items-center">
      {!titleNode ? (<h1
          className="typography-heading-xl [transform:scale(1,1.25)] text-[48px] md:text-[64px] flex items-center text-brand-title-background"
          style={{
            WebkitTextStroke: '2px',
            color: themeColors.titleBackground,
            WebkitTextStrokeColor: themeColors.titleBorder,
          }}
        >
          {title}
        </h1>) : (titleNode)}
        <div className="flex gap-1 items-center">
          <button
            className={`bg-transparent ${
              currentIndex > 0
                ? 'text-brand-main hover:text-brand-highlight'
                : 'text-brand-main cursor-not-allowed pointer-events-none'
            }`}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            aria-label="Previous"
          >
            {currentIndex > 0 ? (
              <ArrowIcon className="text-brand-main" fill="currentColor" style={{ transform: 'rotate(180deg)' }} />
            ) : (
              <DashArrowIcon className="text-brand-main" stroke="currentColor" />
            )}
          </button>
          <button
            className={`bg-transparent ${
              currentIndex < itemCount - 1
                ? 'text-brand-main hover:text-brand-highlight'
                : 'text-brand-main cursor-not-allowed pointer-events-none'
            }`}
            onClick={handleNext}
            disabled={currentIndex === itemCount - 1}
            aria-label="Next"
          >
            {currentIndex < itemCount - 1 ? (
              <ArrowIcon className="text-brand-main" fill="currentColor" />
            ) : (
              <DashArrowIcon className="text-brand-main" style={{ transform: 'rotate(180deg)' }} stroke="currentColor" />
            )}
          </button>
        </div>
      </div>
      <div
        {...bind()}
        className="flex items-stretch sm:items-start overflow-visible"
        style={{
          transform: `translateX(-${currentIndex * (itemWidth + gap)}px)`,
          transition: 'transform 0.3s ease-in-out',
          width: `${itemCount * (itemWidth + gap)}px`,
          gap: `${gap}px`,
        }}
      >
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) return null;
          return (
          <div
            key={index}
            className="flex flex-col flex-[0_0_auto]"
            style={{ width: `${itemWidth}px` }}
          >
            {child}
            </div>
          );
        })}
      </div>
      {lastNode}
      
      {bottomNode}
    </div>
  );
};

export default FeedItemCarousel;
